import React from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { formatPercentage, getRemaining, getTotalContracted, getTotalPayments, getBudgetWarnings } from '../../../utils/budgetUtils';
import { getStatusColor } from '../../../utils';

const ScopeRow = ({ scope, groupIndex, index, onSelectScope }) => {
  const totalPaid = getTotalPayments(scope);
  const remaining = getRemaining(scope);
  
  // Calculate total contracted amount
  const totalContracted = getTotalContracted(scope);
  
  // Determine budget status flags
  const warnings = getBudgetWarnings(scope);
  
  // Budget warning flag - only count no contracts as a warning if there's a budget > 0
  const hasBudgetWarning = warnings.isOutOfBudget || 
    warnings.isContractAmountHigherThanBudget || 
    warnings.isPaymentsMoreThanContracted || 
    warnings.hasNoContracts;
  
  return (
    <tr 
      key={`${groupIndex}-${index}`} 
      className={hasBudgetWarning ? "bg-red-50 dark:bg-red-950/50 hover:bg-red-100 dark:hover:bg-red-900/50 cursor-pointer" : "hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"}
      onClick={() => onSelectScope && onSelectScope(scope.short_name)}
    >
      <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-6">
        <div className="flex flex-col">
          <div 
            className="text-left text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:underline"
          >
            {scope.scope}
          </div>
          
          {/* Subcontractor as secondary text */}
          {scope.subcontractor && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {scope.subcontractor}
            </div>
          )}
          
          {/* Budget warning indicators - only render if at least one warning exists */}
          {(() => {
            // Check if any warnings apply
            const hasWarnings = warnings.isOutOfBudget || 
              warnings.isContractAmountHigherThanBudget || 
              warnings.isPaymentsMoreThanContracted || 
              (warnings.hasNoContracts && scope.budget && scope.budget > 0);
              
            if (!hasWarnings) return null;
            
            return (
              <div className="mt-1 text-xs flex flex-wrap">
                {warnings.isOutOfBudget && (
                  <span className="inline-flex items-center rounded-md bg-red-50 dark:bg-red-900/50 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 ring-1 ring-inset ring-red-600/10 dark:ring-red-500/30 mr-1 mb-1">
                    Out of Budget
                  </span>
                )}
                {warnings.isContractAmountHigherThanBudget && (
                  <span className="inline-flex items-center rounded-md bg-yellow-50 dark:bg-yellow-900/50 px-2 py-1 text-xs font-medium text-yellow-800 dark:text-yellow-300 ring-1 ring-inset ring-yellow-600/20 dark:ring-yellow-500/30 mr-1 mb-1">
                    Contracts > Budget
                  </span>
                )}
                {warnings.isPaymentsMoreThanContracted && (
                  <span className="inline-flex items-center rounded-md bg-orange-50 dark:bg-orange-900/50 px-2 py-1 text-xs font-medium text-orange-700 dark:text-orange-300 ring-1 ring-inset ring-orange-600/10 dark:ring-orange-500/30 mr-1 mb-1">
                    Payments > Contracts
                  </span>
                )}
                {warnings.hasNoContracts && scope.budget && scope.budget > 0 && (
                  <span className="inline-flex items-center rounded-md bg-blue-50 dark:bg-blue-900/50 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-300 ring-1 ring-inset ring-blue-700/10 dark:ring-blue-500/30 mr-1 mb-1">
                    No Contracts
                  </span>
                )}
              </div>
            );
          })()}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
        <span 
          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusColor(scope.status)}`}
        >
          {scope.status}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
        {formatCurrency(scope.budget)}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
        {formatPercentage(scope.percentage_complete)}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
        {formatCurrency(totalContracted)}
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {scope.contracts.length} contract{scope.contracts.length !== 1 ? 's' : ''}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-right">
        {formatCurrency(totalPaid)}
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {scope.payments.length} payment{scope.payments.length !== 1 ? 's' : ''}
        </div>
      </td>
      <td className={`whitespace-nowrap px-3 py-4 text-sm text-right ${warnings.isOutOfBudget ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
        {formatCurrency(remaining)}
      </td>
    </tr>
  );
};

export default ScopeRow;